import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ToastOptions } from '../../helpers/ToastOptions';
import reorderService from '../../services/reorderService';

const DnDContext = createContext();

export const useDnD = () => {
    const context = useContext(DnDContext);
    if (!context) {
        throw new Error('useDnD must be used within a DnDProvider');
    }
    return context;
};

export const DnDProvider = ({ children, onDataChange = null }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragOverItem, setDragOverItem] = useState(null);
    const [dragPreview, setDragPreview] = useState(null);

    // Handle reorder operation - uses switchWithId instead of newPosition
    const handleReorder = useCallback(async (entityType, entityId, switchWithId, scopeType = null, scopeId = null) => {
        try {
            setIsDragging(true);
            
            // Pass switchWithId as the 6th parameter (switchWithId), not as newPosition
            const result = await reorderService.reorderEntity(
                entityType, 
                entityId, 
                null, // newPosition - not used when switchWithId is provided
                scopeType, 
                scopeId,
                switchWithId // switchWithId as 6th parameter
            );

            if (result.success) {
                toast.success(result.message, ToastOptions('success'));
                if (onDataChange) {
                    onDataChange();
                }
            } else {
                toast.error(result.message, ToastOptions('error'));
            }

            return result;
        } catch (error) {
            console.error('Error in handleReorder:', error);
            toast.error('Failed to reorder item', ToastOptions('error'));
            return { success: false, error: error.message };
        } finally {
            setIsDragging(false);
        }
    }, [onDataChange]);

    // Handle transfer operation
    const handleTransfer = useCallback(async (entityType, entityId, targetType, targetId) => {
        try {
            setIsDragging(true);
            
            const result = await reorderService.transferEntity(
                entityType, 
                entityId, 
                targetType, 
                targetId
            );

            if (result.success) {
                toast.success(result.message, ToastOptions('success'));
                if (onDataChange) {
                    onDataChange();
                }
            } else {
                toast.error(result.message, ToastOptions('error'));
            }

            return result;
        } catch (error) {
            console.error('Error in handleTransfer:', error);
            toast.error('Failed to transfer item', ToastOptions('error'));
            return { success: false, error: error.message };
        } finally {
            setIsDragging(false);
        }
    }, [onDataChange]);

    // Handle bulk reorder operation
    const handleBulkReorder = useCallback(async (entityType, reorderData, scopeType = null, scopeId = null) => {
        try {
            setIsDragging(true);
            
            const result = await reorderService.bulkReorderEntities(
                entityType, 
                reorderData, 
                scopeType, 
                scopeId
            );

            if (result.success) {
                toast.success(result.message, ToastOptions('success'));
                if (onDataChange) {
                    onDataChange();
                }
            } else {
                toast.error(result.message, ToastOptions('error'));
            }

            return result;
        } catch (error) {
            console.error('Error in handleBulkReorder:', error);
            toast.error('Failed to bulk reorder items', ToastOptions('error'));
            return { success: false, error: error.message };
        } finally {
            setIsDragging(false);
        }
    }, [onDataChange]);

    // Handle move to end
    const handleMoveToEnd = useCallback(async (entityType, entityId, scopeType = null, scopeId = null) => {
        try {
            setIsDragging(true);
            
            const result = await reorderService.moveToEnd(
                entityType, 
                entityId, 
                scopeType, 
                scopeId
            );

            if (result.success) {
                toast.success(result.message, ToastOptions('success'));
                if (onDataChange) {
                    onDataChange();
                }
            } else {
                toast.error(result.message, ToastOptions('error'));
            }

            return result;
        } catch (error) {
            console.error('Error in handleMoveToEnd:', error);
            toast.error('Failed to move item to end', ToastOptions('error'));
            return { success: false, error: error.message };
        } finally {
            setIsDragging(false);
        }
    }, [onDataChange]);

    // Handle move to beginning
    const handleMoveToBeginning = useCallback(async (entityType, entityId, scopeType = null, scopeId = null) => {
        try {
            setIsDragging(true);
            
            const result = await reorderService.moveToBeginning(
                entityType, 
                entityId, 
                scopeType, 
                scopeId
            );

            if (result.success) {
                toast.success(result.message, ToastOptions('success'));
                if (onDataChange) {
                    onDataChange();
                }
            } else {
                toast.error(result.message, ToastOptions('error'));
            }

            return result;
        } catch (error) {
            console.error('Error in handleMoveToBeginning:', error);
            toast.error('Failed to move item to beginning', ToastOptions('error'));
            return { success: false, error: error.message };
        } finally {
            setIsDragging(false);
        }
    }, [onDataChange]);

    // Handle switch positions between two entities
    const handleSwitchPositions = useCallback(async (entityType, firstEntityId, secondEntityId, scopeType = null, scopeId = null) => {
        try {
            setIsDragging(true);
            
            const result = await reorderService.switchPositions(
                entityType, 
                firstEntityId, 
                secondEntityId,
                scopeType, 
                scopeId
            );

            if (result.success) {
                toast.success(result.message, ToastOptions('success'));
                if (onDataChange) {
                    onDataChange();
                }
            } else {
                toast.error(result.message, ToastOptions('error'));
            }

            return result;
        } catch (error) {
            console.error('Error in handleSwitchPositions:', error);
            toast.error('Failed to switch positions', ToastOptions('error'));
            return { success: false, error: error.message };
        } finally {
            setIsDragging(false);
        }
    }, [onDataChange]);

    // Handle fix gaps
    const handleFixGaps = useCallback(async (entityType, scopeType = null, scopeId = null) => {
        try {
            setIsDragging(true);
            
            const result = await reorderService.fixPositionGaps(
                entityType, 
                scopeType, 
                scopeId
            );

            if (result.success) {
                toast.success(result.message, ToastOptions('success'));
                if (onDataChange) {
                    onDataChange();
                }
            } else {
                toast.error(result.message, ToastOptions('error'));
            }

            return result;
        } catch (error) {
            console.error('Error in handleFixGaps:', error);
            toast.error('Failed to fix position gaps', ToastOptions('error'));
            return { success: false, error: error.message };
        } finally {
            setIsDragging(false);
        }
    }, [onDataChange]);

    // Get position statistics
    const getPositionStats = useCallback(async (entityType, scopeType = null, scopeId = null) => {
        try {
            const result = await reorderService.getPositionStats(
                entityType, 
                scopeType, 
                scopeId
            );

            return result;
        } catch (error) {
            console.error('Error getting position stats:', error);
            return { success: false, error: error.message };
        }
    }, []);

    const value = {
       
        isDragging,
        dragOverItem,
        dragPreview,
        
       
        setIsDragging,
        setDragOverItem,
        setDragPreview,
        
    
        handleReorder,
        handleTransfer,
        handleBulkReorder,
        handleMoveToEnd,
        handleMoveToBeginning,
        handleSwitchPositions,
        handleFixGaps,
        getPositionStats
    };

    return (
        <DnDContext.Provider value={value}>
            {children}
        </DnDContext.Provider>
    );
};
